import { ReadonlyVec2 } from 'gl-matrix';
import { View } from '../view/View';
import { EventHandler } from './EventHandler';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { PlaneView } from '../view/views/PlaneView';

/**
 * Superclass for all the {@link Tool Tools}.\
 * This defines all the methods and general structure of tools.\
 */
export abstract class Tool implements EventHandler {
    abstract toolType: string;
    /**
     * Method to render render the tool in the View
     */
    public render(): void {
        // Empty implementation
    }

    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _button The button that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseMove(
        _coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _delta The direction of the MouseWheel event.
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseWheel(
        _delta: -1 | 1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseDoubleClick event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseDoubleClick(
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * KeyPress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _key The key that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onKeyPress(
        _key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    // End the tool action
    public onDeactivate(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
